/**
 * Logger which logs to console and saves in LogStore
 * @module
 */

/*eslint no-console: 0*/
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var DateEnv = require('../env/date');
var enums = require('../constants/enums');
var Flux = require('../flux');
var JSONEnv = require('../lib/json');

/**
 * @constructor
 */
function LoggerClass() {
  this.logLevel = null;
  this.logMatch = null;
  this.logs = [];
  this.timebase = DateEnv.now();
}

LoggerClass.prototype.__isInitialized = function() {
  return !_.isNull(this.logLevel);
};

/**
 * Set logLevel. If different from current, then call flush.
 * @param {string} logLevelName
 */
LoggerClass.prototype.setLogLevel = function(logLevelName:string) {
  var logLevel = this.__resolveLogLevelName(logLevelName);

  if (__INVARIANT__) {
    require('./invariant').isType('LogLevel', logLevel);
  }

  if (logLevel === null) {
    console.error('Unknown log level: ' + logLevelName);
  } else if (this.logLevel !== logLevel) {
    this.log('Setting log level to ' + logLevel);
    this.logLevel = logLevel;
    this.flush();
  }
};

/**
 * Set log matcher expression
 * @param {string} matcher
 */
LoggerClass.prototype.setLogMatcher = function(matcher:string) {
  if (_.isString(matcher)) {
    this.logMatcher = matcher;
  } else {
    this.logMatcher = '';
  }
  // reset loggroup everytime config is set
  this.logGroup = 0;
};

/**
 * Should a message at this level be logged?
 * @param {LogLevel} logLevel
 * @returns {boolean}
 */
LoggerClass.prototype.shouldLog = function(logLevel:LogLevel): boolean {
  return this.__isInitialized() && this.logLevel >= logLevel;
};

/**
 * Does the log message match the match string
 * @param {string} logLevel
 * @param {string} logMessage
 * @returns {boolean}
 */
LoggerClass.prototype.matchesLogMessage = function(logLevel:string, logMessage:string): boolean {
  var matcher = this.logMatcher;
  if (!this.logMatcher) {
    return true;
  }
  //Don't run match if the group header already matched
  if (this.logGroup) {
    if (logLevel === 'GROUPSTART') {
      this.logGroup++;
    } else if (logLevel === 'GROUPEND') {
      this.logGroup--;
    }
    return true;
  }

  var result = _.some(logMessage, function(message) {
    if (!_.isString(message)) {
      try {
        message = JSONEnv.stringify(message);
      } catch(e) {} // eslint-disable-line
    }
    return _.isString(message) && _.includes(message, matcher);
  });
  //Found a match and this is a group header
  if (result && logLevel === 'GROUPSTART') {
    this.logGroup++ ;
  }
  return result;
};

/**
 * Store log in LogStore
 * @param {LogLevel} logLevel
 * @param {Array.<*>} logArgs
 */
LoggerClass.prototype.storeLog = function(logLevel:LogLevel, logArgs:any[]) {
  var logData = {
    logLevel: logLevel,
    logMessage: logArgs,
  };

  Flux.dispatch(actionTypes.LOG, logData);
};

/**
 * Dump logs from the store to the console.
 */
LoggerClass.prototype.flush = function() {
  // Late require to avoid circular dependency
  var di = require('../di');
  var LogStore = di.get('stores/log');
  //reset loggroup before every flush
  this.logGroup = 0;

  var logs = LogStore.getLogs();
  _.each(logs, _.bind(function(log) {
    this.__logAt(log.logLevel, log.logMessage, true);
  }, this));
};

/**
 * Log a message
 *
 * @param {LogLevel} logLevel level used to get proper console method
 * @param {string|Array<string>} [logMessage] message to apply to console
 * @param {boolean} [replay] if true, don't modify logMessage or store log
 * @private
 */
LoggerClass.prototype.__logAt = function(logLevel:string, logMessage?:string|string[], replay?:boolean) {
  var logger;
  var nominalLogLevel = logLevel;

  if (console) {
    switch(logLevel) {
      case 'GROUPSTART':
        logger = console.groupCollapsed;
        nominalLogLevel = enums.LogLevel.DEBUG;
        break;
      case 'GROUPEND':
        logger = console.groupEnd;
        nominalLogLevel = enums.LogLevel.DEBUG;
        break;
      case enums.LogLevel.ERROR:
        logger = console.error;
        break;
      case enums.LogLevel.WARN:
        logger = console.warn;
        break;
      case enums.LogLevel.DEBUG:
        logger = console.debug;
        break;
      default:
        logger = console.log;
        break;
    }
  }

  try {
    // Only if this is not a "replay" log
    // If __STORE_LOG__ is on, or if logger is uninitialized, store all logs; else store only "current" log level
    if (!replay && (__STORE_LOG__ || !this.__isInitialized() || this.shouldLog(nominalLogLevel))) {
      // Augment with timestamp
      if (_.isArray(logMessage) && _.isString(logMessage[0])) {
        logMessage = this.__augmentArgsWithTimestamp(logMessage);
      }
      // Add to log store
      this.storeLog(logLevel, logMessage);
    }
    // If current log level indicates this should be logged
    if (logger && this.shouldLog(nominalLogLevel) && this.matchesLogMessage(logLevel, logMessage)) {
      // Log to console
      logger.apply(console, logMessage);
    }
  } catch(e) {
    if (console) {
      if (console.error) {
        console.error(e);
      } else {
        console.log(e);
      }
    }
  }
};

/**
 * Log at level DEBUG
 * @param {...*} arguments
 */
LoggerClass.prototype.debug = function() {
  this.__logAt(enums.LogLevel.DEBUG, [].slice.call(arguments));
};

/**
 * Log at level INFO
 * @param {...*} arguments
 */
LoggerClass.prototype.log = function() {
  this.__logAt(enums.LogLevel.INFO, [].slice.call(arguments));
};

/**
 * Log regardless of logLevel
 * @param {...*} arguments
 */
LoggerClass.prototype.logAlways = function() {
  var logArgs = this.__augmentArgsWithTimestamp([].slice.call(arguments));
  if (console && console.log && console.log.apply) {
    console.log.apply(console, logArgs);
  }
  this.storeLog(enums.LogLevel.INFO, logArgs);
};

/**
 * Log at level WARN
 * @param {...*} arguments
 */
LoggerClass.prototype.warn = function() {
  this.__logAt(enums.LogLevel.WARN, [].slice.call(arguments));
};

/**
 * Log at level ERROR, accepts an error or var args
 * @param {Error} [err]
 * @param {...*} arguments
 */
LoggerClass.prototype.error = function(err) {
  var args = [].slice.call(arguments);
  if (args.length === 1 && err.stack) {
    this.__logAt(enums.LogLevel.ERROR, [this.__getTimestamp(), err]);
    this.__logAt(enums.LogLevel.INFO, [err.stack]);
  } else {
    this.__logAt(enums.LogLevel.ERROR, args);
  }
};

/**
 * Open a new log message group (groupCollapsed) if log level DEBUG or above
 * @param {...*} arguments
 */
LoggerClass.prototype.groupCollapsed = function() {
  this.__logAt('GROUPSTART', [].slice.call(arguments));
};

/**
 * Close a log message group
 */
LoggerClass.prototype.groupEnd = function() {
  this.__logAt('GROUPEND', [].slice.call(arguments));
};

/**
 * Prepend the current timestamp etc. to argument list
 * @param {Array.<*>} args
 * @returns {Array.<*>}
 * @private
 */
LoggerClass.prototype.__augmentArgsWithTimestamp = function(args:any[]): any[] {
  var ts = this.__getTimestamp().toString();
  if (ts.length < 6) {
    // Pad timestamp on the left so it has length at least 6
    ts = ('     ' + ts).slice(-6);
  }
  return [ts + '| Optly / ' + args[0]].concat(args.slice(1));
};

/**
 * Get current timestamp (ms since Logger was initialized)
 * @returns {number}
 * @private
 */
LoggerClass.prototype.__getTimestamp = function(): number {
  return this.timebase ? (DateEnv.now() - this.timebase) : 0;
};

/**
 *
 * @param {string} logLevelName
 * @returns {LogLevel|null}
 * @private
 */
LoggerClass.prototype.__resolveLogLevelName = function(logLevelName:string): LogLevel|null {
  if (logLevelName) {
    logLevelName = logLevelName.toUpperCase();

    if (logLevelName === 'TRUE') {
      logLevelName = 'INFO';
    }

    if (logLevelName === 'FALSE') {
      logLevelName = 'OFF';
    }

    if (logLevelName === 'ALL') {
      logLevelName = 'DEBUG';
    }

    if (!_.isUndefined(enums.LogLevel[logLevelName])) {
      return enums.LogLevel[logLevelName];
    }
  }

  return null;
};

/**
 * @type {LoggerClass}
 */
module.exports = new LoggerClass();

if (__TEST__) {
  module.exports.constructor = LoggerClass;
}
